import React, { useState } from 'react';

import { Alert, Badge, Button, Panel, PanelBody, PanelHeader, PanelTitle, Textarea } from '../../../components/ui';
import { BVStudioOutput, BVStudioStatus } from '../types';
import { formatDateLabel, formatStatusLabel, getStatusBadgeVariant } from '../utils';

interface ApprovalControlsProps {
  output: BVStudioOutput;
  canTransition: boolean;
  isPending?: boolean;
  onTransition: (status: BVStudioStatus, rejectionReason?: string) => Promise<void> | void;
}

export function ApprovalControls({
  output,
  canTransition,
  isPending = false,
  onTransition,
}: ApprovalControlsProps) {
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState(output.rejection_reason || '');

  return (
    <Panel>
      <PanelHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <PanelTitle>Review Workflow</PanelTitle>
          <Badge variant={getStatusBadgeVariant(output.status)}>{formatStatusLabel(output.status)}</Badge>
        </div>
      </PanelHeader>
      <PanelBody className="space-y-4">
        <div className="grid gap-3 text-sm text-gray-500 dark:text-gray-400 sm:grid-cols-2">
          <p>Created: {formatDateLabel(output.created_at)}</p>
          <p>Submitted: {formatDateLabel(output.submitted_at)}</p>
          <p>Approved: {formatDateLabel(output.approved_at)}</p>
          <p>Output ID: {output.id}</p>
        </div>

        {output.rejection_reason ? (
          <Alert variant="error" title="Rejection reason">
            {output.rejection_reason}
          </Alert>
        ) : null}

        {!canTransition ? (
          <Alert variant="info" title="Read-only access">
            Your current permissions allow review visibility, but write access is required to submit,
            approve, or reject this output.
          </Alert>
        ) : null}

        {canTransition && (
          <div className="flex flex-wrap gap-3">
            {(output.status === 'draft' || output.status === 'rejected') && (
              <Button disabled={isPending} onClick={() => onTransition('in_review')}>
                Submit for review
              </Button>
            )}

            {output.status === 'in_review' && (
              <>
                <Button disabled={isPending} onClick={() => onTransition('approved')}>
                  Approve
                </Button>
                <Button
                  variant="outline"
                  disabled={isPending}
                  onClick={() => setShowRejectForm(current => !current)}
                >
                  {showRejectForm ? 'Cancel rejection' : 'Reject'}
                </Button>
              </>
            )}
          </div>
        )}

        {canTransition && output.status === 'in_review' && showRejectForm && (
          <div className="space-y-3">
            <Textarea
              value={rejectionReason}
              onChange={event => setRejectionReason(event.target.value)}
              placeholder="Explain why this draft should be revised before approval."
              className="min-h-[120px]"
            />
            <div className="flex justify-end">
              <Button
                variant="destructive"
                disabled={isPending || !rejectionReason.trim()}
                onClick={() => onTransition('rejected', rejectionReason.trim())}
              >
                Confirm rejection
              </Button>
            </div>
          </div>
        )}
      </PanelBody>
    </Panel>
  );
}

export default ApprovalControls;
